import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { FieldWithStatusIndicator } from '@/components/common/item-field-value/FieldWithStatusIndicator';
import type { ItemMultiLinkValue } from '@/lib/api/types/items';
import { LinkButton } from './LinkButton';
import { LinkEditDialog } from './LinkEditDialog';
import type { MultiValueFieldValue } from './MultiValueFieldManager';
import { MultiValueFieldManager } from './MultiValueFieldManager';
import { MULTI_VALUE_MAX_COUNT, validateFieldValue } from './validation';

export interface LinkValue {
  url: string;
  description?: string;
}

interface MultiLinkFieldProps {
  id: string;
  name: string;
  values?: ItemMultiLinkValue[];
  isEditable?: boolean;
  autoFocus?: boolean;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
  onChange: (links: LinkValue[]) => void;
  onValidationChange?: (error: string | null) => void;
}

const toLink = (v: ItemMultiLinkValue): LinkValue => ({
  url: v.MULTI_LINK,
  description: v.description,
});

export const MultiLinkField = ({
  name,
  values = [],
  isEditable = false,
  autoFocus = false,
  isPending = false,
  isSuccess = false,
  isError,
  error,
  onChange,
  onValidationChange,
}: MultiLinkFieldProps) => {
  const { t } = useTranslation();
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(autoFocus && values.length === 0);
  const [editingLinkId, setEditingLinkId] = React.useState<number | null>(null);
  const [editingLinkValue, setEditingLinkValue] = React.useState<LinkValue | undefined>(undefined);

  const handleValidationError = (err: string | null) => {
    setValidationError(err);
    onValidationChange?.(err);
  };

  const handleOnAdd = (link: LinkValue) => {
    if (values.length >= MULTI_VALUE_MAX_COUNT) {
      handleValidationError(
        t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiLinkMaxCount, {
          maxCount: MULTI_VALUE_MAX_COUNT,
        }),
      );
      return;
    }
    const result = validateFieldValue(t, 'LINK', link);
    if (!result.isValid) {
      handleValidationError(result.error ?? null);
      return;
    }
    handleValidationError(null);
    onChange([...values.map(toLink), link]);
  };

  const handleOnUpdate = (valueId: number, link: LinkValue) => {
    const result = validateFieldValue(t, 'LINK', link);
    if (!result.isValid) {
      handleValidationError(result.error ?? null);
      return;
    }
    handleValidationError(null);
    onChange(values.map((v) => (v.id === valueId ? link : toLink(v))));
  };

  const handleEditClick = (linkId: number, link: LinkValue) => {
    setEditingLinkId(linkId);
    setEditingLinkValue(link);
    setDialogOpen(true);
  };

  const handleAddClick = () => {
    if (isPending) return;
    setEditingLinkId(null);
    setEditingLinkValue(undefined);
    setDialogOpen(true);
  };

  const handleSave = (link: LinkValue) => {
    if (editingLinkId === null) {
      handleOnAdd(link);
    } else {
      handleOnUpdate(editingLinkId, link);
    }
    setDialogOpen(false);
    setEditingLinkId(null);
  };

  const fieldValues: MultiValueFieldValue<LinkValue>[] = values.map((v) => ({
    id: v.id,
    value: {
      url: v.MULTI_LINK,
      description: v.description,
    },
  }));

  const resolvedIsError = !!validationError || !!isError;
  const resolvedError = validationError ? new Error(validationError) : (error ?? null);

  return (
    <FieldWithStatusIndicator name={name} isPending={isPending} isSuccess={isSuccess}>
      <div className="flex flex-col gap-1">
        <MultiValueFieldManager<LinkValue>
          values={fieldValues}
          isEditable={isEditable}
          disabled={isPending}
          onRemove={(id) => onChange(values.filter((v) => v.id !== id).map(toLink))}
          maxCount={MULTI_VALUE_MAX_COUNT}
          onAddClick={handleAddClick}
        >
          {(fieldValue) => {
            if (!isEditable) {
              return (
                <LinkButton url={fieldValue.value.url} description={fieldValue.value.description} />
              );
            }

            return (
              <LinkButton
                url={fieldValue.value.url}
                description={fieldValue.value.description}
                onClick={
                  isPending
                    ? undefined
                    : () => fieldValue.id && handleEditClick(fieldValue.id, fieldValue.value)
                }
                isEditable
              />
            );
          }}
        </MultiValueFieldManager>
        {resolvedIsError && resolvedError && (
          <span className="text-xs text-text-error" data-testid="multi-link-field-error">
            {resolvedError.message}
          </span>
        )}
      </div>

      <LinkEditDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingLinkId(null);
        }}
        initialValue={editingLinkValue}
        onSave={handleSave}
        mode={editingLinkId === null ? 'add' : 'edit'}
      />
    </FieldWithStatusIndicator>
  );
};
